// app/src/lib/procurement/util.js — small helpers shared across procurement modules.

import { currentClaims } from '../auth/cognitoClient.js';

/** Require a signed-in user and return userId. Throws with a stable message on failure. */
export async function requireUserId() {
  const claims = await currentClaims();
  if (!claims) {
    const err = new Error('not signed in');
    err.code = 'UNAUTH';
    throw err;
  }
  return claims.sub;
}

/** Normalize a free-text label: lowercase, alnum-only, collapse spaces. */
export function normalize(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '').trim();
}

/** Convert a decimal-string price to integer cents safely. */
export function cents(n) {
  if (n == null || n === '') return 0;
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 100);
}

/** Inverse of cents() — return a dollars number for display only. */
export function dollars(c) {
  return (c || 0) / 100;
}

/** ISO date today (UTC). */
export function isoToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }

/** Add n days to ISO YYYY-MM-DD, return ISO YYYY-MM-DD. */
export function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Days between two ISO dates (a − b). Negative if a is before b. */
export function daysBetween(a, b) {
  const da = new Date(a + 'T00:00:00Z').getTime();
  const db = new Date(b + 'T00:00:00Z').getTime();
  return Math.round((da - db) / (24 * 60 * 60 * 1000));
}

/**
 * Levenshtein distance between two strings.
 * O(m*n) time, O(min(m,n)) space.
 */
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  if (b.length < a.length) { const t = a; a = b; b = t; }
  const m = a.length;
  const n = b.length;
  const dp = new Array(m + 1);
  for (let i = 0; i <= m; i++) dp[i] = i;
  for (let j = 1; j <= n; j++) {
    let prev = dp[0];
    dp[0] = j;
    for (let i = 1; i <= m; i++) {
      const tmp = dp[i];
      dp[i] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[i], dp[i - 1]);
      prev = tmp;
    }
  }
  return dp[m];
}

/**
 * Similarity score 0..1 based on Levenshtein over the longer length.
 */
export function similarity(a, b) {
  const aN = normalize(a);
  const bN = normalize(b);
  if (!aN && !bN) return 1;
  if (!aN || !bN) return 0;
  const dist = levenshtein(aN, bN);
  const max = Math.max(aN.length, bN.length);
  return 1 - dist / max;
}
